// Mock data — typed to mirror future API contracts.
// Swap each `import` for a fetch / queryFn when real endpoints exist.

export type InquiryStatus = "new" | "pending" | "booked" | "archived";
export type ShootStatus = "scheduled" | "active" | "wrapped" | "post";
export type InvoiceStatus = "draft" | "sent" | "paid" | "overdue";
export type GalleryStatus = "uploading" | "proofing" | "selected" | "delivered";
export type ContractStatus = "draft" | "sent" | "signed" | "expired";
export type EditingStage = "cull" | "edit" | "retouch" | "review" | "done";
export type GearStatus = "available" | "on-shoot" | "service" | "missing";

export interface Client {
  id: string;
  name: string;
  company?: string;
  email: string;
  type: "editorial" | "commercial" | "portrait" | "wedding" | "product";
  lifetimeValue: number;
  shoots: number;
  since: string;
}
export interface Inquiry {
  id: string;
  client: string;
  category: string;
  date: string;
  budget: number;
  status: InquiryStatus;
  notes?: string;
}
export interface Shoot {
  id: string;
  title: string;
  client: string;
  location: string;
  callTime: string;
  date: string;
  status: ShootStatus;
  duration: string;
  notes?: string;
}
export interface Invoice {
  id: string;
  number: string;
  client: string;
  amount: number;
  issued: string;
  due: string;
  status: InvoiceStatus;
}
export interface Gallery {
  id: string;
  title: string;
  client: string;
  shootDate: string;
  imageCount: number;
  status: GalleryStatus;
  coverHue: number;
}
export interface Contract {
  id: string;
  title: string;
  client: string;
  sent: string;
  value: number;
  status: ContractStatus;
}
export interface GearItem {
  id: string;
  name: string;
  category: "body" | "lens" | "lighting" | "support" | "audio" | "other";
  serial?: string;
  status: GearStatus;
  lastUsed?: string;
}
export interface EditingTask {
  id: string;
  gallery: string;
  client: string;
  stage: EditingStage;
  due: string;
  imageCount: number;
}
export interface Payment {
  id: string;
  label: string;
  client: string;
  amount: number;
  due: string;
  overdue?: boolean;
}

export const clients: Client[] = [
  { id: "c1", name: "Marcus Halden", company: "Halden & Co", email: "marcus@halden.co", type: "commercial", lifetimeValue: 18400, shoots: 6, since: "2023-04" },
  { id: "c2", name: "Sia Agency", company: "Sia", email: "bookings@sia.studio", type: "editorial", lifetimeValue: 32100, shoots: 11, since: "2022-09" },
  { id: "c3", name: "David Chen", email: "d.chen@gmail.com", type: "portrait", lifetimeValue: 1500, shoots: 1, since: "2024-10" },
  { id: "c4", name: "VOGUE Japan", company: "Condé Nast", email: "production@vogue.jp", type: "editorial", lifetimeValue: 24500, shoots: 4, since: "2024-02" },
  { id: "c5", name: "Arc'teryx", company: "Arc'teryx Equipment", email: "creative@arcteryx.com", type: "commercial", lifetimeValue: 41200, shoots: 7, since: "2023-01" },
  { id: "c6", name: "Nike Inc.", company: "Nike", email: "pm@nike.com", type: "commercial", lifetimeValue: 58000, shoots: 9, since: "2022-06" },
  { id: "c7", name: "Elena Rossi", email: "elena.rossi@studio.it", type: "portrait", lifetimeValue: 3200, shoots: 2, since: "2024-03" },
  { id: "c8", name: "Kinfolk Magazine", company: "Kinfolk", email: "art@kinfolk.com", type: "editorial", lifetimeValue: 9600, shoots: 3, since: "2023-11" },
];

export const inquiries: Inquiry[] = [
  { id: "i1", client: "Marcus Halden", category: "Commercial", date: "Oct 12", budget: 4500, status: "pending", notes: "Team headshots for agency rebrand" },
  { id: "i2", client: "Sia Agency", category: "Editorial", date: "Oct 11", budget: 2200, status: "booked" },
  { id: "i3", client: "David Chen", category: "Portrait", date: "Oct 10", budget: 1500, status: "archived" },
  { id: "i4", client: "Kinfolk Magazine", category: "Lifestyle", date: "Oct 09", budget: 3800, status: "new", notes: "Cotswolds editorial, 2 days on location" },
  { id: "i5", client: "Tesla Inc.", category: "Product", date: "Oct 07", budget: 8800, status: "pending" },
  { id: "i6", client: "Juliana Moore", category: "Portrait", date: "Oct 05", budget: 1200, status: "new" },
];

export const shoots: Shoot[] = [
  { id: "s1", title: "Editorial — VOGUE Japan", client: "VOGUE Japan", location: "Pier 54, Studio B", callTime: "09:00", date: "Today", duration: "6h", status: "active", notes: "Portrait session, 4 looks" },
  { id: "s2", title: "Commercial — Arc'teryx SS25", client: "Arc'teryx", location: "High Line North", callTime: "15:30", date: "Today", duration: "4h", status: "scheduled", notes: "Outdoor campaign, golden hour" },
  { id: "s3", title: "Headshots — Halden & Co", client: "Marcus Halden", location: "Studio A", callTime: "10:00", date: "Oct 16", duration: "3h", status: "scheduled" },
  { id: "s4", title: "Editorial — Kinfolk", client: "Kinfolk Magazine", location: "Cotswolds, UK", callTime: "07:30", date: "Oct 22", duration: "2 days", status: "scheduled" },
  { id: "s5", title: "Product — Tesla Model S", client: "Tesla Inc.", location: "Fremont, CA", callTime: "06:00", date: "Oct 28", duration: "8h", status: "scheduled" },
  { id: "s6", title: "Portrait — Rossi", client: "Elena Rossi", location: "Studio A", callTime: "11:00", date: "Oct 06", duration: "2h", status: "post" },
];

export const invoices: Invoice[] = [
  { id: "v1", number: "INV-0142", client: "Nike Inc.", amount: 8500, issued: "Oct 04", due: "Oct 18", status: "sent" },
  { id: "v2", number: "INV-0141", client: "Arc'teryx", amount: 4500, issued: "Oct 02", due: "Oct 16", status: "sent" },
  { id: "v3", number: "INV-0140", client: "Sia Agency", amount: 2200, issued: "Sep 28", due: "Oct 12", status: "paid" },
  { id: "v4", number: "INV-0139", client: "VOGUE Japan", amount: 6800, issued: "Sep 20", due: "Oct 04", status: "overdue" },
  { id: "v5", number: "INV-0138", client: "Kinfolk Magazine", amount: 3200, issued: "Sep 15", due: "Sep 29", status: "paid" },
  { id: "v6", number: "INV-0143", client: "Elena Rossi", amount: 1600, issued: "—", due: "—", status: "draft" },
];

export const galleries: Gallery[] = [
  { id: "g1", title: "Vogue Oct Issue", client: "VOGUE Japan", shootDate: "Oct 03", imageCount: 142, status: "proofing", coverHue: 30 },
  { id: "g2", title: "Arc'teryx Fall Campaign", client: "Arc'teryx", shootDate: "Sep 28", imageCount: 89, status: "selected", coverHue: 200 },
  { id: "g3", title: "Halden Team Portraits", client: "Marcus Halden", shootDate: "Sep 22", imageCount: 64, status: "delivered", coverHue: 55 },
  { id: "g4", title: "Kinfolk No. 47", client: "Kinfolk Magazine", shootDate: "Sep 14", imageCount: 38, status: "delivered", coverHue: 90 },
  { id: "g5", title: "Rossi Personal Brand", client: "Elena Rossi", shootDate: "Oct 06", imageCount: 51, status: "proofing", coverHue: 15 },
  { id: "g6", title: "Tesla Model S Studio", client: "Tesla Inc.", shootDate: "Oct 08", imageCount: 24, status: "uploading", coverHue: 250 },
];

export const contracts: Contract[] = [
  { id: "ct1", title: "Arc'teryx SS25 Campaign", client: "Arc'teryx", sent: "Oct 08", value: 12000, status: "signed" },
  { id: "ct2", title: "Tesla Product Shoot", client: "Tesla Inc.", sent: "Oct 09", value: 8800, status: "sent" },
  { id: "ct3", title: "Halden Annual Retainer", client: "Marcus Halden", sent: "Oct 02", value: 18000, status: "signed" },
  { id: "ct4", title: "Kinfolk Editorial", client: "Kinfolk Magazine", sent: "—", value: 3800, status: "draft" },
  { id: "ct5", title: "VOGUE Cover Shoot", client: "VOGUE Japan", sent: "Sep 12", value: 22000, status: "expired" },
];

export const gear: GearItem[] = [
  { id: "ge1", name: "Sony A1", category: "body", serial: "A1-7281", status: "on-shoot", lastUsed: "Today" },
  { id: "ge2", name: "Sony A7R V", category: "body", serial: "AR5-9921", status: "available", lastUsed: "Oct 08" },
  { id: "ge3", name: "Sony FE 24-70 GM II", category: "lens", serial: "L-2470-04", status: "on-shoot", lastUsed: "Today" },
  { id: "ge4", name: "Sony FE 85mm f/1.4", category: "lens", serial: "L-85-02", status: "available", lastUsed: "Oct 06" },
  { id: "ge5", name: "Sony FE 16-35 GM", category: "lens", serial: "L-1635-01", status: "available", lastUsed: "Oct 03" },
  { id: "ge6", name: "Profoto B10X", category: "lighting", serial: "PRO-B10-A", status: "service", lastUsed: "Sep 28" },
  { id: "ge7", name: "Profoto B10X", category: "lighting", serial: "PRO-B10-B", status: "available", lastUsed: "Oct 04" },
  { id: "ge8", name: "Manfrotto 504X Tripod", category: "support", status: "available", lastUsed: "Oct 02" },
  { id: "ge9", name: "DJI RS3 Pro", category: "support", serial: "RS3-117", status: "missing", lastUsed: "Sep 14" },
];

export const editing: EditingTask[] = [
  { id: "e1", gallery: "Vogue Oct Issue", client: "VOGUE Japan", stage: "retouch", due: "Oct 16", imageCount: 142 },
  { id: "e2", gallery: "Arc'teryx Fall Campaign", client: "Arc'teryx", stage: "review", due: "Oct 15", imageCount: 89 },
  { id: "e3", gallery: "Rossi Personal Brand", client: "Elena Rossi", stage: "edit", due: "Oct 18", imageCount: 51 },
  { id: "e4", gallery: "Tesla Model S Studio", client: "Tesla Inc.", stage: "cull", due: "Oct 20", imageCount: 24 },
  { id: "e5", gallery: "Halden Team Portraits", client: "Marcus Halden", stage: "done", due: "Oct 10", imageCount: 64 },
];

export const upcomingPayments: Payment[] = [
  { id: "p1", label: "Nike Creative Fee", client: "Nike Inc.", amount: 8500, due: "Oct 18" },
  { id: "p2", label: "Lens Hire Reimburse", client: "Arc'teryx", amount: 450, due: "Oct 20" },
  { id: "p3", label: "VOGUE Cover Balance", client: "VOGUE Japan", amount: 6800, due: "Oct 04", overdue: true },
];

export const kpis = {
  revenueMtd: 14240,
  revenueChangePct: 12,
  bookedShoots: 8,
  pendingShoots: 3,
  pendingInvoices: 4120.5,
  pendingClients: 4,
  awaitingProofs: 3,
  avgTurnaround: 4.2,
};

export const studio = {
  name: "Thorne Studio",
  ownerName: "Elias Thorne",
  ownerEmail: "elias@thorne.studio",
  ownerInitials: "ET",
  plan: "Pro",
};

export const revenueByMonth = [
  { m: "May", v: 8200 },
  { m: "Jun", v: 11400 },
  { m: "Jul", v: 9800 },
  { m: "Aug", v: 13200 },
  { m: "Sep", v: 12700 },
  { m: "Oct", v: 14240 },
];

export const topPackages = [
  { name: "Editorial Day Rate", bookings: 14, revenue: 42000 },
  { name: "Commercial Half Day", bookings: 9, revenue: 27000 },
  { name: "Headshot Session", bookings: 22, revenue: 13200 },
  { name: "Product Studio Day", bookings: 6, revenue: 18000 },
];

export function fmtUsd(n: number, opts: { compact?: boolean } = {}) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: opts.compact ? "compact" : "standard",
    maximumFractionDigits: opts.compact ? 1 : 2,
  }).format(n);
}
